/**
 * Tree loading and error states
 */

import React from 'react';
import { motion } from 'framer-motion';

export interface TreeLoadingStateProps {
  message?: string;
}

export const TreeLoadingState: React.FC<TreeLoadingStateProps> = ({
  message = 'Loading equipment...'
}) => (
  <div className="w-full h-full flex flex-col items-center justify-center p-8">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center space-y-4"
    >
      <div className="w-16 h-16 mx-auto">
        <svg
          className="animate-spin text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
      <p className="text-gray-600 dark:text-gray-400 font-medium">{message}</p>
    </motion.div>
  </div>
);

export interface TreeErrorStateProps {
  error: Error | null;
  onRetry?: () => void;
}

export const TreeErrorState: React.FC<TreeErrorStateProps> = ({ error, onRetry }) => (
  <div className="w-full h-full flex flex-col items-center justify-center p-8">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center space-y-4 max-w-md"
    >
      <div className="w-16 h-16 mx-auto text-red-500">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          />
        </svg>
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Failed to Load Equipment
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {error?.message || 'An unexpected error occurred while loading the equipment data.'}
        </p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          Try Again
        </button>
      )}
    </motion.div>
  </div>
);
